import { Injectable } from "@angular/core";
import { obtenerFechaEscrita, obtenerHora } from "@shared/utils/date.utils";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { SessionService } from "./session.service";
import { Usuario } from "@models/usuario.model";
import { Prestador } from "@models/prestador.model";
import { Licencia } from "@models/licencia.model";

@Injectable({
	providedIn: "root",
})
export class PdfService {
	usuario: Usuario; // Rut del usuario sin su digito verificador
	constructor(private sessionService: SessionService) {
		(pdfMake as any).vfs = pdfFonts.vfs;
		this.usuario = this.sessionService.getSesionActual();
	}

	async generarPdfResolucion(Licencias: Licencia[], prestador: Prestador): Promise<string> {
		// Si la imagen está en assets
		const imageBase64 = await this.getBase64ImageFromURL(
			"assets/images/gob.png"
		);
		const timbreBase64 = await this.getBase64ImageFromURL(
			"assets/images/timbres/viña.png"
		);
		const rows: string[][] = Licencias.map((l, i) => [
			(i + 1).toString(),
			l.numero.toString(),
			l.getFechaEmisionSinHora() ?? "",
			l.trabajador.getRutConGuion(),
		]);

		const header = this.getHeader(imageBase64);
		const docDefinition = {
			pageMargins: [40, 140, 40, 60], // espacio para el header
			header: header,
			content: [
				{
					columns: [
						{ text: "" }, // columna izquierda vacía, ocupa la mitad izquierda
						{
							stack: [
								{
									text: "RESOLUCIÓN EXENTA N° [resolexta]",
									bold: true,
									margin: [0, 0, 0, 15],
								},
								{
									columns: [
										{
											text: "MATERIA:",
											bold: true,
											width: "auto",
											margin: [0, 0, 2, 0],
										},
										{
											text: "Cita a audiencia conforme al artículo 2 de la Ley 20.585.",
										},
									],
									margin: [0, 0, 0, 15],
								},
								{
									text:
										"Santiago, " +
										obtenerFechaEscrita(new Date()),
									bold: true,
								},
							],
							width: "50%",
						},
					],
					margin: [0, 0, 0, 15],
				},
				{
					columns: [
						{ text: "De:", width: 50 },
						{
							stack: [
								{
									text: this.usuario.getNombreCompleto(),
									width: "auto",
								},
								{ text: `Presidente (a)`, width: "auto" },
								{ text: `[compin]`, width: "auto" },
							],
						},
					],
					margin: [0, 0, 0, 15],
				},
				{
					columns: [
						{ text: "A:", width: 50 },
						{
							stack: [
								{
									text: prestador.getNombreCompleto(),
									width: "auto",
								},
								{ text: prestador.email, width: "auto" },
							],
						},
					],
					margin: [0, 0, 0, 15],
				},
				{
					columns: [
						{ text: "", width: "50%" }, // espacio vacío mitad izquierda
						{
							text: "VISTO:",
							bold: true,
							width: "auto",
							margin: [0, 0, 2, 0],
						},
						{
							text: "Lo dispuesto en el artículo 2 de la Ley",
							alignment: "right",
						},
					],
				},
				{
					text: "20.585; y",
					margin: [0, 0, 0, 15],
				},
				this.getLinea("CONSIDERANDO:"),
				...this.getParrafo(
					["1.", "Que, el artículo 2 de la Ley 20.585 faculta a"],
					"esta Comisión de Medicina Preventiva e Invalidez para fiscalizar a los profesionales emisores de licencias médicas."
				),
				...this.getParrafo(
					["2.", "Que,  en   casos   excepcionales,  dicha  ley"],
					"permite la citación del profesional emisor a una audiencia para aclarar aspectos relacionados con el otorgamiento de las licencias médicas emitidas."
				),
				...this.getParrafo(
					["3.", "Que,   conforme   al   análisis  de  su   caso,"],
					"usted ha sido seleccionado puesto que:",
					false
				),
				{
					columns: [
						{ text: "", width: "50%" }, // espacio vacío mitad izquierda
						{
							stack: [{ text: `- [selección]`, width: "auto" }],
						},
					],
					margin: [0, 0, 0, 15],
				},
				...this.getParrafo(
					["4.", "Que,    atendida    las    siguientes    causas"],
					"excepcionales, se ha citado a ud. a audiencia:",
					false
				),
				{
					columns: [
						{ text: "", width: "50%" }, // espacio vacío mitad izquierda
						{
							stack: [{ text: `- [excepción]`, width: "auto" }],
						},
					],
					margin: [0, 0, 0, 15],
				},
				...this.getParrafo(
					["5.", "Que, en  virtud  de lo expuesto, se  resuelve"],
					"lo siguiente:",
					false,
					true
				),
				/* Página 2 */
				this.getLinea("RESOLUCIÓN"),
				...this.getParrafo(
					[
						"1.",
						`Se         cita          a         usted,         don`,
					],
					`${prestador.getNombreCompleto()}, Cédula de Identidad N°${prestador.getRutConGuion()}, a una audiencia presencial para aclarar aspectos relacionados con el otorgamiento de las siguientes licencias médicas emitidas:`
				),
				this.getTabla(
					[
						"N°",
						"Folio licencia médica",
						"Fecha de emisión",
						"RUN del paciente",
					],
					rows
				),
				...this.getParrafo(
					["2.", "La    audiencia   se    efectuará   el   día"],
					`${obtenerFechaEscrita(
						new Date("2025-09-18 13:52:00")
					)} a las ${obtenerHora(
						new Date("2025-09-18 13:52:00")
					)} horas, en dependencia de esta Comisión de Medicina Preventiva e Invalidez, ubicada en [direccion], comuna [comuna]. El tiempo de espera será de media hora, una vez transcurrido el tiempo, se tendrá como no presentado.`
				),
				...this.getParrafo(
					["3.", "La  presente  citación  se  realiza  bajo"],
					"apercibimiento de aplicar alguna de las sanciones descritas en el artículo 2 de la Ley 20.585."
				),
				...this.getParrafo(
					["4.", "Notifíquese  la  presente  resolución al"],
					"correo electrónico del profesional emisor, conforme a las normas de la Ley 20.585."
				),
				{ text: "", margin: [0, 0, 0, 50] },
				{ text: "[firma]", alignment: "center" },
				{ text: this.usuario.getNombreCompleto(), alignment: "center" },
				{ text: "PRESIDENTE(A)", alignment: "center" },
                {
                    image: timbreBase64, // timbre
                    width: 80,
                    absolutePosition: { x: 150, y: 490 } // lado izquiedo de la firma
                },
				{
					text: "[compin]",
					alignment: "center",
					margin: [0, 0, 0, 50],
				},
				{ text: "DISTRIBUCIÓN:", bold: true },
				{ text: "-  Interesado", margin: [25, 0, 0, 0] },
			],
		};
		//pdfMake.createPdf(docDefinition).open(); // Abrir en otra pestaña
		// Guardar en base64 para enviar al back
		let base64: string = await new Promise((resolve) => {
      pdfMake.createPdf(docDefinition).getBase64((data: string) => {
        resolve(data);
      });
    });
    
    
    /* pdfMake.createPdf(docDefinition).getBase64((data: string) => {
			base64 = data;
		}); */
		return base64;
	}

	private getHeader(imageBase64: string) {
		return {
			margin: [40, 20, 40, 0],
			columns: [
				{
					stack: [
						{ image: imageBase64, width: 60, alignment: "left" },
						{
							text: "SEREMI DE SALUD [seremi]",
							fontSize: 9,
							bold: true,
						},
						{
							text: "COMISIÓN DE MEDICINA PREVENTIVA E INVALIDEZ",
							fontSize: 9,
							bold: true,
						},
						{ text: "REGIÓN [region]", fontSize: 9, bold: true },
					],
					width: "auto",
				},
			],
		};
	}

	private getParrafo(
		primeraLinea: string[],
		siguientesLineas: string,
		marginBotton: boolean = true,
		saltoPAgina: boolean = false
	) {
		return [
			{
				columns: [
					{ text: "", width: "50%" }, // espacio vacío mitad izquierda
					{
						text: primeraLinea[0],
						bold: true,
						width: "auto",
						margin: [0, 0, 10, 0],
						alignment: "justify",
					},
					{
						text: primeraLinea[1],
						alignment: "right",
					},
				],
			},
			{
				text: siguientesLineas,
				margin: [0, 0, 0, marginBotton ? 15 : 0],
				alignment: "justify",
				pageBreak: saltoPAgina ? "after" : "",
			},
		];
	}

	private getLinea(texto) {
		return {
			columns: [
				{ text: "", width: "50%" }, // espacio vacío mitad izquierda
				{ text: texto, bold: true, margin: [0, 0, 0, 15] },
			],
		};
	}

	private getTabla(header: string[], body: string[][]) {
		const filasHeader = header.map((h) => ({
			text: h,
			bold: true,
			fillColor: "#eeeeee",
		}));
		return {
			table: {
				headerRows: 1,
				widths: ["auto", "*", "*", "*"], // Puedes usar '*' para ancho flexible, o números para puntos
				body: [
					// fila de encabezados
					filasHeader,
					// filas con datos
					...body,
				],
			},
			margin: [0, 0, 0, 15],
		};
	}

	private getBase64ImageFromURL(url: string): Promise<string> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.setAttribute("crossOrigin", "anonymous");
			img.onload = () => {
				const canvas = document.createElement("canvas");
				canvas.width = img.width;
				canvas.height = img.height;
				const ctx = canvas.getContext("2d");
				ctx?.drawImage(img, 0, 0);
				resolve(canvas.toDataURL("image/png"));
			};
			img.onerror = (error) => reject(error);
			img.src = url;
		});
	}
}
